import { useEffect, useMemo, useState } from "react";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LinkSimple, MagnifyingGlass, CheckCircle, Warning, ArrowDown, ArrowUp, ArrowsDownUp } from "@phosphor-icons/react";

const TIPO_BADGE = {
  gasto: ["gasto", "bg-orange-50 text-orange-700 border-orange-200"],
  traspaso_in: ["traspaso", "bg-sky-50 text-sky-700 border-sky-200"],
  traspaso_out: ["traspaso", "bg-sky-50 text-sky-700 border-sky-200"],
  pago_pasivo: ["pasivo", "bg-violet-50 text-violet-700 border-violet-200"],
};

/**
 * Asocia uno o varios movimientos importados (Excel) a una o varias operaciones del
 * sistema (cobros / pagos / gastos / traspasos / pagos de pasivos). Soporta N:1, 1:M y N:M.
 */
export default function ConciliarMovimientoDialog({ mov, movs: movsProp, onClose, onDone }) {
  const movs = useMemo(() => (movsProp && movsProp.length ? movsProp : mov ? [mov] : []), [mov, movsProp]);
  const base = movs[0] || null;
  const open = movs.length > 0;
  const totalBanco = useMemo(() => movs.reduce((a, m) => a + Number(m.monto || 0), 0), [movs]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [soloCaja, setSoloCaja] = useState(true);
  const [ordenFecha, setOrdenFecha] = useState(null);

  useEffect(() => {
    if (!base) return;
    setSel([]); setQ(""); setData(null); setLoading(true); setSoloCaja(true); setOrdenFecha(null);
    api.get(`/conciliacion/candidatos/${base.id}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudieron cargar los candidatos"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base?.id]);

  const todos = data?.candidatos || [];
  const enOtrasCajas = useMemo(() => todos.filter((c) => c.caja_id !== base?.caja_id).length, [todos, base]);
  const lista = useMemo(() => {
    const all = soloCaja ? todos.filter((c) => c.caja_id === base?.caja_id) : todos;
    const t = q.trim().toLowerCase();
    const ordenar = (arr) => {
      if (!ordenFecha) return arr;
      return [...arr].sort((a, b) => {
        const fa = a.fecha || "", fb = b.fecha || "";
        return ordenFecha === "desc" ? (fa < fb ? 1 : fa > fb ? -1 : 0) : (fa < fb ? -1 : fa > fb ? 1 : 0);
      });
    };
    if (!t) return ordenar(all);
    const num = Number(t.replace(/\./g, "").replace(",", "."));
    return ordenar(all.filter((c) =>
      (c.contraparte_nombre || "").toLowerCase().includes(t)
      || (c.caja_nombre || "").toLowerCase().includes(t)
      || (c.comprobantes || []).some((x) => x.toLowerCase().includes(t))
      || (c.concepto || "").toLowerCase().includes(t)
      || (!Number.isNaN(num) && Math.abs(Number(c.monto_caja) - num) < 0.01)
    ));
  }, [todos, q, soloCaja, base, ordenFecha]);

  const esIngreso = base?.tipo === "ingreso";
  const label = esIngreso ? "cobro" : "pago";
  const seleccionados = useMemo(() => todos.filter((c) => sel.includes(c.id)), [todos, sel]);
  const totalSistema = seleccionados.reduce((a, c) => a + Number(c.monto_caja || 0), 0);
  const dif = Math.round((totalBanco - totalSistema) * 100) / 100;
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const asociar = async () => {
    if (!seleccionados.length) return;
    setSaving(true);
    try {
      const multi = movs.length > 1 || seleccionados.length > 1;
      const { data: r } = multi
        ? await api.post("/conciliacion/asociar-multi", { mov_ids: movs.map((m) => m.id), cobro_pago_ids: sel })
        : await api.post("/conciliacion/asociar", { mov_id: base.id, cobro_pago_id: sel[0] });
      toast.success(
        Math.abs(r.diferencia || 0) >= 0.01
          ? `Conciliado. Se generó un movimiento por la diferencia (${fmtMoney(Math.abs(r.diferencia), base.moneda)}) para clasificar.`
          : multi
            ? `Conciliado: ${movs.length} movimiento(s) del banco ↔ ${seleccionados.length} operación(es) del sistema.`
            : "Conciliado: queda un solo movimiento en la caja."
      );
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo conciliar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl rounded-sm p-0 overflow-hidden" data-testid="conciliar-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="font-heading text-[16px] flex items-center gap-2">
            <LinkSimple size={18} weight="bold" className="text-[#6B1F24]" />
            Asociar {esIngreso ? "ingreso a cobros / traspasos" : "egreso a pagos, gastos, pasivos o traspasos"}
            {movs.length > 1 && <span className="text-[11px] font-mono bg-sky-50 text-sky-700 border border-sky-200 rounded-sm px-1.5 py-0.5" data-testid="conciliar-multi-badge">{movs.length} movimientos del banco</span>}
          </DialogTitle>
          {base && (
            <div className="mt-2 border border-[#E5E5E5] bg-neutral-50 rounded-sm px-3 py-2 text-[12px] space-y-1" data-testid="conciliar-mov-resumen">
              {movs.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="font-mono text-[11px] text-[#666] mr-2">{fmtDate(m.fecha)} · {m.caja_nombre}</span>
                    <span className="truncate" title={m.concepto}>{m.concepto}</span>
                  </div>
                  <div className={`font-mono font-semibold shrink-0 ${esIngreso ? "num-pos" : "num-neg"}`}>
                    {esIngreso ? "+" : "-"} {fmtMoney(m.monto, m.moneda)}
                  </div>
                </div>
              ))}
              {movs.length > 1 && (
                <div className="flex justify-between border-t pt-1 font-mono text-[12px]">
                  <span className="uppercase text-[10px] text-[#666]">Total banco</span>
                  <b data-testid="conciliar-total-banco">{fmtMoney(totalBanco, base.moneda)}</b>
                </div>
              )}
            </div>
          )}
        </DialogHeader>

        <div className="px-5 py-3 border-b flex items-center gap-2">
          <MagnifyingGlass size={14} className="text-neutral-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Buscar ${label} por cliente, caja, monto o comprobante…`}
            className="h-8 rounded-sm text-[12px]"
            data-testid="conciliar-buscar"
          />
          <span className="text-[11px] font-mono text-neutral-500 shrink-0" title={data?.fecha_desde ? `Sólo operaciones desde ${fmtDate(data.fecha_desde)}` : ""}>
            {lista.length} sin conciliar{data?.fecha_desde ? ` · desde ${fmtDate(data.fecha_desde)}` : ""}
          </span>
        </div>
        <div className="px-5 py-2 border-b bg-neutral-50 flex items-center gap-1 text-[11px]" data-testid="conciliar-filtro-caja">
          <button
            type="button"
            onClick={() => setSoloCaja(true)}
            data-testid="conciliar-solo-caja"
            className={`px-2.5 h-7 rounded-md border font-medium transition ${soloCaja ? "bg-[#6B1F24] border-[#6B1F24] text-white" : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-100"}`}
          >
            Sólo esta caja
          </button>
          <button
            type="button"
            onClick={() => setSoloCaja(false)}
            data-testid="conciliar-todas-cajas"
            className={`px-2.5 h-7 rounded-md border font-medium transition ${!soloCaja ? "bg-[#6B1F24] border-[#6B1F24] text-white" : "bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-100"}`}
          >
            Todas las cajas <span className="font-mono opacity-80">(+{enOtrasCajas})</span>
          </button>
          <span className="ml-auto text-neutral-500 font-mono">
            Podés tildar más de una operación si el banco las resumió en un solo movimiento.
          </span>
        </div>

        <div className="max-h-[360px] overflow-y-auto">
          {loading && <div className="py-10 text-center text-[13px] text-neutral-500">Cargando {label}s…</div>}
          {!loading && lista.length === 0 && (
            <div className="py-10 text-center text-[13px] text-neutral-500" data-testid="conciliar-vacio">
              No hay {esIngreso ? "cobros ni traspasos recibidos" : "pagos, gastos, pasivos ni traspasos enviados"} sin conciliar{q ? " con ese criterio" : ""}{soloCaja ? ` en la caja "${base?.caja_nombre}"` : ""}.
              {soloCaja && enOtrasCajas > 0 && (
                <div className="mt-2">
                  <button type="button" onClick={() => setSoloCaja(false)} className="text-[#6B1F24] underline decoration-dotted" data-testid="conciliar-ver-otras">
                    Ver {enOtrasCajas} en otras cajas
                  </button>
                </div>
              )}
            </div>
          )}
          {!loading && lista.length > 0 && (
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase font-mono text-[#666] bg-neutral-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 w-8"></th>
                  <th className="px-4 py-2 text-left w-24">
                    <button
                      type="button"
                      onClick={() => setOrdenFecha((o) => (o === "desc" ? "asc" : o === "asc" ? null : "desc"))}
                      data-testid="conciliar-orden-fecha"
                      className={`inline-flex items-center gap-1 uppercase hover:text-[#6B1F24] ${ordenFecha ? "text-[#6B1F24]" : ""}`}
                    >
                      Fecha
                      {ordenFecha === "desc" ? <ArrowDown size={11} weight="bold" /> : ordenFecha === "asc" ? <ArrowUp size={11} weight="bold" /> : <ArrowsDownUp size={11} className="opacity-50" />}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-left">{esIngreso ? "Cliente" : "Proveedor / Beneficiario"}</th>
                  <th className="px-4 py-2 text-left w-32">Caja</th>
                  <th className="px-4 py-2 text-left w-40">Comprobante</th>
                  <th className="px-4 py-2 text-right w-32">Monto</th>
                  <th className="px-4 py-2 text-right w-28">Dif.</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((c) => {
                  const on = sel.includes(c.id);
                  const badge = TIPO_BADGE[c.tipo];
                  return (
                    <tr
                      key={c.id}
                      onClick={() => toggle(c.id)}
                      data-testid={`conciliar-cand-${c.id}`}
                      className={`border-t cursor-pointer transition-colors ${on ? "bg-[#F5F1E9]" : "hover:bg-neutral-50"}`}
                    >
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm border ${on ? "bg-[#6B1F24] border-[#6B1F24] text-white" : "border-neutral-400"}`}>
                          {on && <CheckCircle size={10} weight="fill" />}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-[12px]">
                        {fmtDate(c.fecha)}
                        {c.dias_dif != null && c.dias_dif > 0 && <div className="text-[10px] text-neutral-400">±{c.dias_dif}d</div>}
                      </td>
                      <td className="px-4 py-2">
                        <div className="truncate max-w-[220px]">
                          {badge && <span className={`mr-1 text-[9px] uppercase font-mono border rounded-sm px-1 py-0.5 ${badge[1]}`} data-testid={`cand-${badge[0]}-${c.id}`}>{badge[0]}</span>}
                          {c.contraparte_nombre}
                        </div>
                        {c.concepto && <div className="text-[10px] text-neutral-500 truncate max-w-[220px]">{c.concepto}</div>}
                      </td>
                      <td className={`px-4 py-2 text-[12px] ${c.caja_id !== base?.caja_id ? "text-amber-700 font-medium" : "text-[#666]"}`} title={c.caja_id !== base?.caja_id ? "Registrado en otra caja" : ""}>{c.caja_nombre}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-[#666]">
                        {(c.comprobantes_detalle || []).length
                          ? c.comprobantes_detalle.map((cd, i) => (
                              <div key={i}>{cd.label}{cd.total != null ? <span className="text-neutral-400"> · {fmtMoney(cd.total, c.moneda)}</span> : null}</div>
                            ))
                          : <span className="text-neutral-400">sin imputar</span>}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">{fmtMoney(c.monto_caja, c.moneda)}</td>
                      <td className="px-4 py-2 text-right font-mono text-[12px]">
                        {Math.abs(totalBanco - Number(c.monto_caja)) < 0.01
                          ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={12} weight="fill" /> exacto</span>
                          : <span className="text-amber-700">{totalBanco - Number(c.monto_caja) > 0 ? "+" : ""}{fmtMoney(totalBanco - Number(c.monto_caja), c.moneda)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t flex-col sm:flex-row sm:items-center gap-2">
          <div className="text-[11px] text-neutral-600 flex-1" data-testid="conciliar-ayuda">
            {seleccionados.length > 0 && (
              <div className="font-mono text-[12px] mb-0.5" data-testid="conciliar-totales">
                {seleccionados.length} operación(es) · sistema <b>{fmtMoney(totalSistema, base?.moneda)}</b> · banco <b>{fmtMoney(totalBanco, base?.moneda)}</b>
                {Math.abs(dif) < 0.01 && <span className="ml-2 text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> coincide</span>}
              </div>
            )}
            {seleccionados.length > 0 && Math.abs(dif) >= 0.01 ? (
              <span className="inline-flex items-start gap-1 text-amber-800">
                <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                Los montos difieren en {fmtMoney(Math.abs(dif), base?.moneda)}: se va a generar un movimiento por esa diferencia (comisión, redondeo) para que lo clasifiques.
              </span>
            ) : (
              <>Se conservan los movimientos del sistema y se eliminan los del Excel. Podés desconciliar después.</>
            )}
          </div>
          <Button variant="outline" onClick={onClose} className="rounded-sm h-9" data-testid="conciliar-cancelar">Cancelar</Button>
          <Button onClick={asociar} disabled={!seleccionados.length || saving} className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]" data-testid="conciliar-confirmar">
            <LinkSimple size={14} className="mr-1" /> {saving ? "Conciliando…" : seleccionados.length > 1 ? `Asociar ${seleccionados.length}` : "Asociar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConciliadoBadge({ mov, onDesconciliar }) {
  if (!mov?.conciliado) return null;
  const c = mov.conciliacion || {};
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5 ml-2"
      title={`Conciliado con extracto: ${c.fecha_banco || ""} · ${c.concepto_banco || ""}${c.diferencia ? ` · dif ${c.diferencia}` : ""}${c.cantidad_operaciones > 1 ? ` · ${c.cantidad_operaciones} operaciones` : ""}`}
      data-testid={`conciliado-badge-${mov.id}`}
    >
      <LinkSimple size={10} weight="bold" /> Conciliado
      {onDesconciliar && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDesconciliar(c.conciliacion_id); }}
          className="ml-1 underline decoration-dotted hover:text-emerald-900 normal-case"
          data-testid={`desconciliar-${mov.id}`}
        >
          deshacer
        </button>
      )}
    </span>
  );
}
